<template>
  <div class="market-products">
    <div class="page-header d-flex align-items-center">
      <!-- Back arrow with click -->
      <span
        class="me-2 text-decoration-none"
        role="button"
        @click="$router.push('/')"
        data-bs-toggle="tooltip"
        data-bs-placement="bottom"
        title="Back to Home"
      >
        <i class="bi bi-arrow-left-circle fs-4"></i>
      </span>

      <!-- Header text -->
      <div>
        <h5>Explore Products</h5>
        <p>Find the products you need from our marketplace.</p>
      </div>
    </div>

    <div class="search-bar">
      <i class="bi bi-search"></i>

      <input v-model="search" type="search" placeholder="Search products..." />

      <button v-if="search" @click="search = ''">
        <i class="bi bi-x-lg"></i>
      </button>
    </div>

    <!-- Product Categories -->
    <div class="category-list">
      <button
        v-for="category in categories"
        :key="category.id"
        :class="{
          active: selectedCategory === String(category.name),
        }"
        @click="selectCategory(category)"
      >
        {{ category.name }}
      </button>
    </div>

    <!-- Products -->
    <div class="row g-3">
      <div v-if="loading" class="col-12 empty-state">
        <div class="spinner-border text-success"></div>
        <p class="mt-2">Loading products...</p>
      </div>

      <div v-else-if="error" class="col-12 empty-state">
        <i class="bi bi-exclamation-circle"></i>
        <h6>Unable to load products</h6>
        <p>{{ error }}</p>

        <button class="btn btn-sm btn-success" @click="getProducts">
          Try Again
        </button>
      </div>

      <template v-else>
        <div
          v-for="product in filteredProducts"
          :key="product.id"
          class="col-12 col-sm-6 col-lg-3"
        >
          <article class="product-card h-100" @click="openProduct(product)">
            <div class="product-image">
              <img
                :src="product.image"
                :alt="product.name"
                loading="lazy"
                @error="handleImageError"
              />
            </div>

            <div class="product-info">
              <small>{{ product.product_name }}</small>

              <h6>{{ product.name }}</h6>

              <span class="seller-count d-block mb-1">
                <i class="bi bi-shop me-1"></i> {{ product.shop_code }}
              </span>

              <!-- New stock level -->
              <span
                class="seller-count d-block mb-1 fw-bold"
                :class="{
                  'text-danger': product.total_items < 1,
                  'text-success': product.total_items > 10,
                }"
              >
                <i class="bi bi-box-seam me-1"></i>
                Stock count: {{ product.total_items }}
              </span>

              <div class="view-product mt-2">
                View more from the seller
                <i class="bi bi-arrow-right"></i>
              </div>
            </div>
          </article>
        </div>

        <div v-if="filteredProducts.length === 0" class="col-12 empty-state">
          <i class="bi bi-search"></i>
          <h6>No products found</h6>
          <p>Try another search or category.</p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Config from "../Helpers/Config";

const API_BASE_URL = Config.API_BASE_URL;

export default {
  name: "MarketProducts",

  data() {
    return {
      search: "",
      selectedCategory: "",
      favourites: [],
      categories: [],
      products: [],
      loading: false,
      error: "",
    };
  },

  computed: {
    filteredProducts() {
      const query = this.search.trim().toLowerCase();

      return this.products.filter((product) => {
        const matchesSearch =
          product.name?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query) ||
          product.product_name?.toLowerCase().includes(query);

        return matchesSearch;
      });
    },
  },

  watch: {
    "$route.query.category": {
      immediate: true,

      handler(category) {
        this.selectedCategory = category ? String(category) : "";

        this.getProducts();
      },
    },
  },

  mounted() {
    this.getCategories();
  },

  methods: {
    // Select category and update URL
    selectCategory(category) {
      const selected = category ? String(category.name) : "";

      if (selected === this.selectedCategory) {
        return;
      }

      this.$router.push({
        name: "ProductsPage",
        query: selected ? { category: selected } : {},
      });
    },

    // Fetch products
    async getProducts() {
      this.loading = true;
      this.error = "";

      try {
        const category = this.$route.query.category;

        const url = category
          ? `${API_BASE_URL}/product/${encodeURIComponent(category)}`
          : `${API_BASE_URL}/product`;

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP Error: ${response.status}`);
        }

        const result = await response.json();

        const data = Array.isArray(result)
          ? result
          : Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.products)
          ? result.products
          : [];

        this.products = data.map((product) => ({
          ...product,
          image: product.product_image
            ? `https://services.libaaxc.com/inventory/${product.product_image}`
            : "https://placehold.co/500x400?text=Product",
        }));
      } catch (error) {
        console.error("Products loading error:", error);

        this.error =
          error.message || "An error occurred while loading products.";
      } finally {
        this.loading = false;
      }
    },

    // Fetch categories
    async getCategories() {
      const url = `${API_BASE_URL}/categories/products`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(
            `API request failed: ${response.status} ${response.statusText}`,
          );
        }

        const result = await response.json();

        const data = Array.isArray(result)
          ? result
          : Array.isArray(result.data)
          ? result.data
          : [];

        this.categories = data.map((item) => ({
          id: item.id,
          name: item.category_code ?? "",
          description: item.category_name ?? "",
          image: item.feature_image ?? "",
        }));

        console.log("Product categories:", this.categories);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    },

    // Open product sellers
    openProduct(product) {
      this.$router.push({
        name: "ProductSellers",
        params: {
          id: product.id,
        },
      });
    },

    // Toggle favourites
    toggleFavourite(id) {
      this.favourites = this.favourites.includes(id)
        ? this.favourites.filter((item) => item !== id)
        : [...this.favourites, id];
    },

    // Handle missing product images
    handleImageError(event) {
      event.target.src = "https://placehold.co/500x400?text=Product";
    },
  },
};
</script>

<style scoped>
.market-products {
  min-height: 100%;
  padding: 22px;
  background: #f6f7fb;
  color: #333;
}

.page-header h5 {
  margin: 0 0 5px;
  font-size: 20px;
  font-weight: 600;
}

.page-header p {
  margin: 0 0 18px;
  color: #777;
  font-size: 13px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 650px;
  height: 44px;
  padding: 0 13px;
  margin-bottom: 18px;
  border: 1px solid #e5e9ed;
  border-radius: 7px;
  background: #fff;
}

.search-bar > i {
  color: #777;
}

.search-bar input {
  flex: 1;
  min-width: 0;
  border: 0;
  outline: 0;
  font-size: 13px;
}

.search-bar button {
  border: 0;
  background: transparent;
  color: #777;
  cursor: pointer;
}

.category-list {
  display: flex;
  gap: 9px;
  overflow-x: auto;
  padding-bottom: 15px;
  scrollbar-width: thin;
}

.category-list button {
  flex-shrink: 0;
  padding: 8px 15px;
  border: 1px solid #e2e7e5;
  border-radius: 20px;
  background: #fff;
  color: #555;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-list button:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.category-list button.active {
  border-color: var(--primary-color);
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
  box-shadow: 0 2px 7px rgba(0, 0, 0, 0.12);
}

.product-card {
  overflow: hidden;
  min-width: 0;
  border: 1px solid #e8ecef;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.product-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 18px #0000000d;
}

.product-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
}

.product-image img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  transition: transform 0.25s;
}

.product-card:hover .product-image img {
  transform: scale(1.04);
}

.wishlist-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #555;
  cursor: pointer;
}

.wishlist-btn.selected {
  color: #dc3545;
}

.product-info {
  padding: 13px;
}

.product-info > small {
  color: var(--primary-color);
  font-size: 11px;
}

.product-info h6 {
  margin: 7px 0;
  font-size: 14px;
  font-weight: 600;
}

.seller-count {
  color: #777;
  font-size: 11px;
}

.view-product {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 11px;
  margin-top: 12px;
  border-top: 1px solid #f0f2f4;
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 600;
}

.empty-state {
  padding: 50px 20px;
  border-radius: 8px;
  background: #fff;
  text-align: center;
}

.empty-state > i {
  color: var(--primary-color);
  font-size: 30px;
}

.empty-state h6 {
  margin-top: 12px;
}

.empty-state p {
  color: #777;
  font-size: 13px;
}

@media (max-width: 750px) {
  .market-products {
    padding: 14px;
  }

  .product-image {
    height: 155px;
  }
}

@media (max-width: 420px) {
  .product-image {
    height: 125px;
  }

  .product-info {
    padding: 10px;
  }
}
</style>
